/**
 * MaestroFieldControl — the Create Media Plan form's own input, rendered in Maestro
 *
 * Purpose: ORCH-412 — every field in Maestro's wizard offers the same control the
 *   Create Media Plan form offers for it (text input, locale-aware number input,
 *   master-data dropdown, date picker), so a field Maestro could not fill is never
 *   a dead end. Labels, option lists, precision and the multiselect shape all come
 *   from the field's metadata, so a field added to the view in the Metadata
 *   Manager gets its control here with no Maestro change.
 * Architecture: Reads the plan view's own Field row through `fieldFor` (ORCH-374)
 *   and renders it with `getFieldRenderer(datatype)` — the registry the form uses.
 *   This component is presentational and controlled: it never decides *when* a
 *   value reaches the plan. That rule belongs to the card state that renders it
 *   (MaestroMappingsStepper, ORCH-376 / ORCH-412 §3.3). Number fields are wrapped
 *   in the plan's currency context the way ApprovalNumberField does, so Budget
 *   shows the same symbol as the form. No error / touched state is passed — the
 *   plan form's validation stays authoritative (ORCH-412 R3).
 * Dependencies: metadata-form/FieldRenderers (getFieldRenderer),
 *   template-form/PlanningCurrencyContext,
 *   ApprovalNumberField (resolveCurrencyCode), services/mdmCacheService
 */

import React, { useMemo } from 'react';
import { getFieldRenderer } from '../metadata-form/FieldRenderers';
import type { ViewField } from '../metadata-form/types';
import { PlanningCurrencyProvider } from '../template-form/PlanningCurrencyContext';
import { useMDMValues } from '../../services/mdmCacheService';
import { PLAN_CURRENCY_FD_KEY, resolveCurrencyCode } from './ApprovalNumberField';

/** Datatypes the number renderer handles — the only ones that need the currency symbol. */
const NUMERIC_DATATYPES = new Set(['currency', 'decimal', 'number']);

/** Stable empty filter object for useMDMValues (avoids a new identity each render). */
const NO_MDM_FILTERS = {};

/**
 * True when a view field can be answered from a Maestro card.
 *
 * ORCH-412 R2: a file upload inside a wizard step is not wanted (the Media Brief
 * itself is a file field), so `file` falls back to the card's message.
 */
export const isMaestroEditableField = (field: ViewField | undefined): field is ViewField =>
  !!field && field.field_definition?.datatype?.toLowerCase() !== 'file';

export interface MaestroFieldControlProps {
  /** The plan view's Field row (from `useCreatePlanFieldLabels().fieldFor`). */
  field: ViewField;
  tenantId: string;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  /** The plan form's current Currency value, so a number field's symbol matches the form. */
  currencyFieldValue?: unknown;
}

const MaestroFieldControl: React.FC<MaestroFieldControlProps> = ({
  field,
  tenantId,
  value,
  onChange,
  disabled,
  currencyFieldValue,
}) => {
  const datatype = field.field_definition.datatype;
  const isNumeric = NUMERIC_DATATYPES.has(datatype.toLowerCase());
  const Renderer = getFieldRenderer(datatype);

  const { data: currencyOptions = [] } = useMDMValues(
    PLAN_CURRENCY_FD_KEY,
    tenantId,
    NO_MDM_FILTERS,
    { enabled: isNumeric }
  );
  const currencyCode = useMemo(
    () => (isNumeric ? resolveCurrencyCode(currencyFieldValue, currencyOptions) : null),
    [isNumeric, currencyFieldValue, currencyOptions]
  );

  return (
    <PlanningCurrencyProvider value={currencyCode}>
      <Renderer
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        inputProps={{ 'data-testid': `maestro-field-input-${field.fielddef_id}` }}
      />
    </PlanningCurrencyProvider>
  );
};

export default MaestroFieldControl;
